// FILE: src/features/authSlice.js
// Sign-in is two steps (D1): password (+ authenticator code when enabled),
// then the one-time code we email. The server is the source of truth for the
// session; when it ends one (idle, replaced, locked, expired) the client
// forgets the user and records why (B-6).
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { clearProfile } from "./profileSlice";
import { apiFetch } from "../services/api";

// Codes that mean "you are no longer signed in".
export const SESSION_END_CODES = [
  "SESSION_IDLE",
  "SESSION_REPLACED",
  "INVALID_SESSION",
  "TOKEN_NOT_FOUND",
  "INVALID_TOKEN",
  "USER_NOT_FOUND",
  "ACCOUNT_LOCKED"
];
// COURSE_EXPIRED is not one of them: an expired student stays signed in and
// sees the enrolment-ended page, with support reachable (WP-3).

async function postJson(path, body) {
  const res = await apiFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));
  return { res, data };
}

const reject = (rejectWithValue, data, fallback) =>
  rejectWithValue({ code: data?.code || fallback, ...data });

/** Step 1: { email, password, totp? } → { otpRequired, loginTicket, destination, expiresInSec } */
export const startLogin = createAsyncThunk(
  "auth/startLogin",
  async ({ email, password, totp }, { rejectWithValue }) => {
    try {
      const { res, data } = await postJson("/api/auth/login", {
        email,
        password,
        ...(totp ? { twoFactorToken: totp } : {})
      });
      if (!res.ok) return reject(rejectWithValue, data, "INVALID_CREDENTIALS");
      return data;
    } catch {
      return rejectWithValue({ code: "NETWORK" });
    }
  }
);

/** Step 2: { loginTicket, code } → { user } and the session cookie. */
export const login = createAsyncThunk(
  "auth/login",
  async ({ loginTicket, code }, { rejectWithValue }) => {
    try {
      const { res, data } = await postJson("/api/auth/login/verify-otp", { loginTicket, code });
      if (!res.ok) return reject(rejectWithValue, data, "INVALID_OTP");
      return data;
    } catch {
      return rejectWithValue({ code: "NETWORK" });
    }
  }
);

export const resendLoginCode = createAsyncThunk(
  "auth/resendLoginCode",
  async ({ loginTicket }, { rejectWithValue }) => {
    try {
      const { res, data } = await postJson("/api/auth/login/resend-otp", { loginTicket });
      if (!res.ok) return reject(rejectWithValue, data, "OTP_EXPIRED");
      return data;
    } catch {
      return rejectWithValue({ code: "NETWORK" });
    }
  }
);

export const checkInvite = createAsyncThunk(
  "auth/checkInvite",
  async (token, { rejectWithValue }) => {
    if (!token) return rejectWithValue("INVALID_TOKEN");
    try {
      const resp = await apiFetch(`/api/auth/check-invite?token=${encodeURIComponent(token)}`);
      const data = await resp.json();
      if (!resp.ok || !data.valid) return rejectWithValue(data.code || "INVALID_TOKEN");
      return data; // { valid, email, firstName }
    } catch {
      return rejectWithValue("NETWORK");
    }
  }
);

export const checkSession = createAsyncThunk(
  "auth/checkSession",
  async (_, { rejectWithValue }) => {
    try {
      const resp = await apiFetch("/api/auth/me");
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return rejectWithValue(err.code || "INVALID_SESSION");
      }
      return await resp.json();
    } catch {
      return rejectWithValue("NETWORK");
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const resp = await apiFetch("/api/auth/logout", { method: "POST" });
      dispatch(clearProfile());
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        return rejectWithValue(errData.code || "LOGOUT_FAILED");
      }
      return await resp.json();
    } catch (err) {
      return rejectWithValue("NETWORK");
    }
  }
);

const initialState = {
  user: null,
  role: null,
  loading: false,
  error: null,
  // Why the server ended the last session (e.g. "SESSION_IDLE"); shown on /login.
  endReason: null,
  invitationCheck: { loading: false, error: null, valid: false, email: null }
};

const errorCode = (payload, fallback) =>
  (payload && typeof payload === "object" ? payload.code : payload) || fallback;

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /** The server (or the idle timer) ended the session. */
    sessionEnded(state, action) {
      state.user = null;
      state.role = null;
      state.endReason =
        action.payload && "code" in action.payload ? action.payload.code : "INVALID_SESSION";
    },
    clearEndReason(state) {
      state.endReason = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(startLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startLogin.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(startLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = errorCode(action.payload, "INVALID_CREDENTIALS");
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.role = action.payload.user.role;
        state.endReason = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = errorCode(action.payload, "INVALID_OTP");
      });

    builder
      .addCase(checkInvite.pending, (state) => {
        state.invitationCheck = { loading: true, error: null, valid: false, email: null };
      })
      .addCase(checkInvite.fulfilled, (state, action) => {
        state.invitationCheck = { loading: false, error: null, valid: true, email: action.payload.email };
      })
      .addCase(checkInvite.rejected, (state, action) => {
        state.invitationCheck = { loading: false, error: action.payload || "INVALID_TOKEN", valid: false, email: null };
      })
      .addCase(checkSession.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.role = action.payload.user.role;
      })
      .addCase(checkSession.rejected, (state, action) => {
        // A dead session is forgotten; a network blip is not a logout.
        if (SESSION_END_CODES.includes(action.payload)) {
          state.user = null;
          state.role = null;
          state.endReason = action.payload;
        }
      });

    builder
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.role = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = errorCode(action.payload, "LOGOUT_FAILED");
        // Fail closed: forget the user even if the server call failed.
        state.user = null;
        state.role = null;
      });
  }
});

export const { sessionEnded, clearEndReason } = authSlice.actions;
// Kept for existing callers: a local logout is a session end with no reason.
export const logout = () => sessionEnded({ code: null });
export default authSlice.reducer;
